/**
 * VesselContext - Single source of truth for vessel data
 * COMPLIANT with ANCHOR MODE standards
 * 
 * Uses legacyUtils for all localStorage access
 * Standard field names: id, name, type (not vesselId, vesselName, vesselType)
 * Under 400 lines
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { legacyGet, legacySet, legacyRemove } from '../storage/legacyUtils';
// FT-365 v3.1 hotfix: self-healing raw reader for currentVesselId.
import { readCurrentVesselIdRaw } from '../utils/readCurrentVesselId';
import {
  migrateToMultiVessel,
  migrateCrewData,
  initializeVesselStorage,
  cleanupVesselStorage
} from '../storage/migrations/migrateToMultiVessel';
// MIGRATION TEMPORARILY DISABLED - WAS HANGING APP
// import { migrateVesselIdToUUID } from '../storage/migrations/migrateVesselIdToUUID';
// import { migrateStorageKeys } from '../storage/migrations/migrateStorageKeys';
import { migrateVesselRAsToFleet } from '../storage/migrations/migrateVesselRAsToFleet';  // FT-116 REWRITE
import * as storageRouter from '../lib/storageRouter';
import { toOrgScopedKey } from '../lib/storage';
import { track1 } from '../utils/track1Telemetry';

const VesselContext = createContext();

export const useVessel = () => {
  const context = useContext(VesselContext);
  if (!context) {
    throw new Error('useVessel must be used within a VesselProvider');
  }
  return context;
};

// Migrate vessel settings from quoted keys to normalized keys
// FT-365 v3 Layer C site 6: writes now go through storageRouter.writeJSON at
// the org-scoped key so the migrated row lives on the correct composite key
// and cross-org readers don't see the migrated value.
function migrateVesselSettingsKeyIfNeeded(vesselId) {
  const id = String(vesselId).replace(/^"|"$/g, '');
  const goodKey = `vesselSettings_${id}`;
  const badKey = `vesselSettings_"${id}"`;

  try {
    const bad = JSON.parse(localStorage.getItem(badKey) || 'null');
    const good = JSON.parse(localStorage.getItem(goodKey) || 'null');

    if (bad && !good) {
      // Route through storageRouter at the org-scoped key so migrations don't
      // bypass the FT-05 org-scoping layer.
      storageRouter.writeJSON(toOrgScopedKey(goodKey), bad).catch((err) => {
        console.warn('[VesselContext] FT-365 v3 Layer C site 6: migrateVesselSettingsKey writeJSON failed', err);
        // Fallback to LS at the same org-scoped key so the migration still lands.
        try { localStorage.setItem(toOrgScopedKey(goodKey), JSON.stringify(bad)); } catch (_) { /* non-fatal */ }
      });
      localStorage.removeItem(badKey);

    }
  } catch (error) {

  }
}

// Migrate legacy heat source values to WBC3-compliant options
// WBC3 only recognizes: electric, gas, liquid_fuel
// FT-365 v3 Layer C site 7: route through storageRouter at the org-scoped key.
function migrateLegacyHeatSources(vesselId) {
  const key = `vesselSettings_${vesselId}`;

  try {
    // Read at the org-scoped key so we mutate the row that will actually be read.
    const orgScopedKey = toOrgScopedKey(key);
    const settings = JSON.parse(localStorage.getItem(orgScopedKey) || localStorage.getItem(key) || 'null');

    if (settings && settings.galleyHeatSource) {
      const legacyValue = settings.galleyHeatSource;
      let migrated = false;

      // Convert legacy values to WBC3-compliant equivalents
      if (legacyValue === 'open_flame') {
        settings.galleyHeatSource = 'liquid_fuel'; // Most similar to open flame

        migrated = true;
      } else if (legacyValue === 'solid_fuel') {
        settings.galleyHeatSource = 'liquid_fuel'; // Solid fuel burns like liquid fuel

        migrated = true;
      }

      if (migrated) {
        storageRouter.writeJSON(orgScopedKey, settings).catch((err) => {
          console.warn('[VesselContext] FT-365 v3 Layer C site 7: migrateLegacyHeatSources writeJSON failed', err);
          try { localStorage.setItem(orgScopedKey, JSON.stringify(settings)); } catch (_) { /* non-fatal */ }
        });
      }
    }
  } catch (error) {

  }
}

export const VesselProvider = ({ children }) => {
  // FT-302: initial value is `undefined` (not `[]`) so `vessels.length === 0`
  // NEVER renders the "Add Your First Vessel" gate before storage has been read.
  // TailwindShell already gates on `vessels === undefined || vessels === null`
  // (renders LoadingScreen) BEFORE the `vessels.length === 0` check. The `[]`
  // default said "we checked, zero vessels" (triggering the gate); `undefined`
  // says "not yet checked" (triggering the spinner). Semantically correct and
  // eliminates any first-paint race regardless of device/bfcache behaviour.
  // Internal consumers below (memos + isMultiVessel/vesselCount + effect deps)
  // are guarded with `(vessels || [])` so the undefined initial state cannot
  // crash the provider itself.
  const [vessels, setVessels] = useState(undefined);
  const [currentVesselId, setCurrentVesselId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // FT-158: Settings version counter to invalidate currentVessel memoization when settings change
  const [settingsVersion, setSettingsVersion] = useState(0);

  // FT-365 v3.5: initial-hydration flag. `true` from mount until AuthGate
  // finishes its first checkAuthState pass (dispatches sr:auth:initialHydrated
  // in both the success and catch paths). While true, consumers of the empty-
  // vessels gate (TailwindShell "Add your first vessel") should render a
  // loading indicator instead — otherwise the async gap between Layer B's
  // wipeUserCaches and getVessels() resolving flashes the welcome gate on
  // account-change sign-ins. Also protected by a 5s safety timeout so a
  // failed/missing event can never leave the UI stuck hydrating forever.
  const [isHydrating, setIsHydrating] = useState(true);

  // FT-263: Per-vessel settings cache, sentinel-based (mirrors useStorageState.js:152):
  //   undefined  → not yet read from IDB; LS fallback may be consulted (one-shot)
  //   null       → read from IDB, no record exists; LS fallback NOT consulted
  //   object     → read from IDB, valid settings
  // Shape: { [vesselId]: undefined | null | object }
  const [settingsCache, setSettingsCache] = useState({});

  // FT-263: mirrors FT-245's liveVesselIdRef in UniversalSyncBridge.js (reference only).
  // The sr:sync:hydrate listener is registered once on mount, so its closure would
  // capture currentVesselId by value. A ref bridges the gap when the user switches
  // vessels mid-flight (A→B race) without forcing re-registration of the listener.
  const currentVesselIdRef = useRef(null);
  useEffect(() => { currentVesselIdRef.current = currentVesselId; }, [currentVesselId]);

  // Initialize vessels and current vessel
  useEffect(() => {
    const initializeVessels = () => {
      try {
        // MIGRATION TEMPORARILY DISABLED - WAS HANGING APP
        // const vesselIdMigration = migrateVesselIdToUUID();

        // Run multi-vessel migration (existing logic)
        const vesselsList = migrateToMultiVessel();
        setVessels(vesselsList);

        // MIGRATION TEMPORARILY DISABLED
        // if (vesselIdMigration) {
        //   migrateStorageKeys(
        //     vesselIdMigration.oldVesselId,
        //     vesselIdMigration.newVesselId,
        //     ['crew', 'equipment_register', 'maintenance_history']
        //   );
        // }

        // Get current vessel ID (stored as plain string, not JSON).
        // FT-365 v3.1 hotfix: use self-healing raw reader so any legacy
        // double-JSON encoding from a brief FT-365 v3 exposure is unwrapped
        // AND rewritten as a plain string in one shot.
        const normalizedVesselId = readCurrentVesselIdRaw();

        // Fix crew data mismatch if needed
        if (normalizedVesselId) {
          migrateCrewData(normalizedVesselId);
          migrateVesselSettingsKeyIfNeeded(normalizedVesselId);
          migrateLegacyHeatSources(normalizedVesselId); // Auto-convert legacy heat sources
        }

        // FT-116 REWRITE: Migrate vessel-scoped RAs to fleet storage
        // Runs once, backs up old keys (doesn't delete), sets migration flag
        migrateVesselRAsToFleet();

        if (normalizedVesselId && vesselsList.some(v => v.id === normalizedVesselId)) {
          setCurrentVesselId(normalizedVesselId);
          // currentVesselId already in localStorage as plain string - no additional write needed
        } else if (vesselsList.length > 0) {
          // FT-365 v3 Layer C site 4: DO NOT auto-select vessels[0] here.
          // On a cross-user login this was the primary bleed vector — super_admin's
          // vessels[0] would surface on Ryan's first mount and vice versa. AuthGate
          // Layer A#1 reconciliation now sets currentVesselId from the server-provided
          // priority (preferredVesselId → previous → serverVessels[0] → picker gate)
          // BEFORE VesselProvider mounts on the reload. If we still reach this branch,
          // it means either (a) no auth reconciliation ran (dev/no-auth mode), or
          // (b) auth ran but produced no valid vessel — the picker gate flag should
          // be set. Fall back to vessels[0] only when no picker gate is requested.
          const showPickerGate = (() => {
            try {
              // eslint-disable-next-line local/no-localstorage-in-react, no-restricted-syntax -- FT-365 v3 Layer A#1 boundary read
              return localStorage.getItem('sr.vessel.showPickerGate') === 'true';
            } catch (_) { return false; }
          })();
          if (!showPickerGate) {
            const firstVesselId = vesselsList[0].id;
            setCurrentVesselId(firstVesselId);
            // FT-365 v3.1 hotfix: raw write (not legacySet). Downstream
            // readers use raw LS reads; double-encoding via legacySet caused
            // the FT-64 vessel-guard warning storm freeze in prod.
            localStorage.setItem('currentVesselId', firstVesselId);
          }
          // else: leave currentVesselId null so UI can render a vessel picker gate.
        }
      } catch (error) {

      } finally {
        setIsLoading(false);
      }
    };

    initializeVessels();
  }, []);

  // FT-263: IDB-aware loader. toOrgScopedKey is applied at the leaf storage
  // modules (storage.js writeJSON / storageIndexedDB.js), NOT in storageRouter.
  // Always promotes the cache entry out of `undefined`, even on a null IDB hit,
  // so the LS fallback in readSettings is consulted at most once per vessel per
  // session — mirrors the `idbValue === undefined` pattern in useStorageState.js:152.
  const loadSettingsFromIdb = useCallback(async (vesselId) => {
    if (!vesselId) return;
    try {
      // FT-365 v3 Layer B#2: read at the org-scoped key with enforcement so
      // any stale row keyed under a DIFFERENT org's UUID is rejected before
      // it reaches the vessel switcher / dashboard header. Was the top
      // render-path exposure identified by the code-lens investigation.
      const fromIdb = await storageRouter.readJSON(
        toOrgScopedKey(`vesselSettings_${vesselId}`),
        null,
        { enforceOrgScope: true }
      );
      // SENTINEL: null IDB result still moves the cache out of `undefined`.
      // null cache entry means "IDB checked, no record" — different from "not yet checked".
      setSettingsCache(prev => ({ ...prev, [vesselId]: fromIdb ?? null }));
      setSettingsVersion(v => v + 1);  // re-invalidates all the memos already keyed on settingsVersion
    } catch (e) {
      console.warn('[VesselContext] IDB settings load failed:', e);
      // On read error, leave the cache entry as `undefined` so LS fallback can serve
      // legacy users on next call. Do NOT poison the cache with null on failure.
    }
  }, []);

  // FT-263: sentinel-aware read used by all four READ callsites + the
  // read-modify-write merge in updateVessel (5 total). Replaces direct
  // legacyGet(<id>, 'vesselSettings') calls.
  const readSettings = useCallback((vesselId) => {
    if (!vesselId) return null;
    const cached = settingsCache[vesselId];
    // SENTINEL: cached === undefined → not yet read, fall through to LS (one-shot, legacy compat)
    //           cached === null      → IDB checked, no record — return {} (NOT LS, never again)
    //           cached === object    → IDB hit — return as-is
    if (cached === undefined) {
      // First-and-only LS fallback. Next loadSettingsFromIdb / hydrate promotes the
      // cache entry out of `undefined`, so this path is consulted at most once per
      // vessel per session — true one-shot, not a permanent shadow path.
      return legacyGet(vesselId, 'vesselSettings');
    }
    return cached ?? {};
  }, [settingsCache]);

  // FT-263: warm IDB cache for the currently-active vessel whenever it changes.
  // Guarantees fresh cloud-state on every vessel switch (A→B→A staleness contract:
  // "fresh as of last IDB sync" — see §7-4 of CODING-BRIEF-v1.1.md).
  useEffect(() => {
    if (currentVesselId) {
      loadSettingsFromIdb(currentVesselId);
    }
  }, [currentVesselId, loadSettingsFromIdb]);

  // FT-263: warm IDB cache for ALL known vessels so the enriched dropdown
  // (FT-231) renders correct names/types/categories without waiting for a
  // vessel switch. Reads are cheap (small JSON blobs per vessel).
  useEffect(() => {
    // FT-302: guard against `vessels === undefined` initial state.
    (vessels || []).forEach(v => {
      if (v && v.id && !(v.id in settingsCache)) {
        loadSettingsFromIdb(v.id);
      }
    });
    // settingsCache deliberately excluded — we only want this to fire when the
    // vessels list itself changes. Per-vessel loadSettingsFromIdb is guarded by
    // the `v.id in settingsCache` check so we don't re-fetch already-warmed entries.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vessels, loadSettingsFromIdb]);

  // FT-365 v3.5: listen for the initial-hydration signal from AuthGate.
  // AuthGate dispatches `sr:auth:initialHydrated` at the end of its first
  // checkAuthState pass (both success and catch paths). We flip isHydrating
  // to false so TailwindShell's welcome gate can render normally after that
  // point. A 5s safety timeout guarantees we never stay stuck hydrating even
  // if the event never fires (e.g. dev/offline paths that short-circuit).
  //
  // FT-365 v3.5.1: ALSO re-read LS.vessels before flipping isHydrating.
  // The Layer D remount (VesselProviderKeyedByAuth key flip on
  // sr:auth:userChanged) can cause a fresh VesselContext to mount AFTER
  // AuthGate's wipeUserCaches({preserveAuth: true}) has cleared LS.vessels
  // but BEFORE getVessels()'s legacySet write lands. That fresh context
  // misses the sr:storage:set event fired by legacySet (mounted after it
  // dispatched), so its internal `vessels` state stays at the empty array
  // it captured at mount, even though LS.vessels now has Ryan's data.
  // Result: `vessels.length === 0 && !isHydrating` renders the welcome
  // gate and hangs. Re-reading LS here catches that race — idempotent for
  // the healthy case, corrective for the remount-race case.
  //
  // FT-365 Option B (2026-07): even under Option B (no Layer D remount),
  // this handler still re-reads LS.vessels on sr:auth:initialHydrated.
  // Redundant in the healthy path (window.location.reload gives us a
  // fresh mount that reads LS directly) but protects against any future
  // auth path that violates the reload invariant — cheap belt-and-braces.
  // See feedback_auth_boundary_reload_invariant.md
  useEffect(() => {
    // FT-365 Option B Phase 2: track whether the initialHydrated event fired
    // before the 5s safety timeout so silent regressions of AuthGate's dispatch
    // path (e.g. checkAuthState never resolves, event listener race) surface in
    // telemetry rather than as a mysterious welcome-gate flash. `sawEvent` is
    // captured in both closures via the enclosing useEffect scope.
    let sawEvent = false;

    const onHydrated = () => {
      sawEvent = true;
      // Snapshot fresh vessels from LS so we can validate currentVesselId
      // against the same list we're setting into state (avoids A→B races
      // where LS.vessels changed between our two reads).
      let freshVessels = null;
      try {
        freshVessels = legacyGet(null, 'vessels');
        if (Array.isArray(freshVessels)) {
          setVessels(freshVessels);
        }
      } catch (err) {
        // Non-fatal: fall through to the isHydrating flip so the UI can
        // still recover via the 5s safety timeout / normal render path.
        console.warn('[VesselContext] FT-365 v3.5.1: LS refresh on hydration failed (non-fatal):', err);
      }

      // FT-365 Option B (Phase 1 extension): ALSO re-read LS.currentVesselId
      // so it stays in sync with LS.vessels. Under Option B the reload gives
      // us a fresh mount, but initializeVessels runs BEFORE AuthGate's
      // FT-223/Layer A#1 finishes writing LS.currentVesselId. Without this
      // refresh, `currentVesselId` React state stays null even after LS
      // is populated — TailwindDashboard gates on `currentVessel &&` and
      // per-vessel modules never render. Guard with vessels-list membership
      // so we never point currentVesselId at an id that isn't in the list.
      try {
        const freshCurrentVesselId = readCurrentVesselIdRaw();
        if (freshCurrentVesselId) {
          const validIds = Array.isArray(freshVessels)
            ? new Set(freshVessels.map((v) => v && v.id).filter(Boolean))
            : null;
          if (!validIds || validIds.has(freshCurrentVesselId)) {
            setCurrentVesselId(freshCurrentVesselId);
          }
        }
      } catch (err) {
        console.warn('[VesselContext] FT-365 Option B: currentVesselId refresh on hydration failed (non-fatal):', err);
      }

      setIsHydrating(false);
    };
    window.addEventListener('sr:auth:initialHydrated', onHydrated);
    const timeout = setTimeout(() => {
      // FT-365 Option B Phase 2: the 5s safety timeout should almost never
      // fire in a healthy session — AuthGate's checkAuthState dispatches
      // sr:auth:initialHydrated in both success + catch paths within a few
      // hundred ms. If we hit this timeout with sawEvent=false the dispatch
      // path itself has broken (regression signal).
      try {
        track1.log('[HYDRATION-TIMEOUT]', { hadEventBefore: sawEvent });
      } catch (_) { /* non-fatal */ }
      setIsHydrating(false);
    }, 5000);
    return () => {
      window.removeEventListener('sr:auth:initialHydrated', onHydrated);
      clearTimeout(timeout);
    };
  }, []);

  // FT-263: cross-device / cross-tab receive — listen for sync hydrate events
  // dispatched by UniversalSyncBridge after a cloud pull. Updates settingsCache
  // for the matching vessel so all four memos re-render with fresh data.
  useEffect(() => {
    const handleHydrate = (event) => {
      const detail = (event && event.detail) || {};
      if (detail.module !== 'vesselSettings') return;
      const incomingVesselId = detail.vesselId;
      const incoming = detail.value;
      if (!incomingVesselId || incoming == null) return;

      // FT-263 A→B race guard: bridge dispatches with detail.vesselId == the
      // active vessel at dispatch-time (UniversalSyncBridge.js:123). If the user
      // has switched A→B between cloud-read and dispatch, an A-payload could be
      // keyed under B's id. Drop any event whose vesselId no longer matches the
      // live current vessel. Mirrors the FT-245 liveVesselIdRef pattern.
      if (incomingVesselId !== currentVesselIdRef.current) return;

      setSettingsCache(prev => ({ ...prev, [incomingVesselId]: incoming }));
      setSettingsVersion(v => v + 1);  // re-render all consumers
    };
    window.addEventListener('sr:sync:hydrate', handleHydrate);
    return () => window.removeEventListener('sr:sync:hydrate', handleHydrate);
  }, []);

  // Get vessel settings (compatibility layer) - defined FIRST to avoid TDZ
  const getVesselSettings = useCallback(() => {
    if (!currentVesselId) return null;
    return readSettings(currentVesselId);
  }, [currentVesselId, readSettings]);

  // Get current vessel object - merged with settings for complete data
  const getCurrentVessel = useCallback(() => {
    // FT-302: guard against `vessels === undefined` initial state.
    const vessel = (vessels || []).find(v => v.id === currentVesselId);
    if (!vessel) return null;

    // FT-263: read via sentinel-aware cache (IDB-backed). Falls back to LS
    // exactly once on first call before loadSettingsFromIdb populates the cache.
    const settings = readSettings(currentVesselId);

    // Canonical names preferred, with safe fallbacks
    // FT-231: Also check settings?.type (not just vesselType) as that's where type is stored
    // FT-263 (2026-05-31): precedence flipped. Settings is now the canonical
    // store (populated via sr:sync:hydrate from cloud + IDB-backed cache). The
    // `vessels` array is loaded once via legacyGet/LS at mount and never updated
    // by the hydrate listener — so vessel.name reflects the boot-time value.
    // Prefer settings.name first so cross-device edits are visible immediately.
    const name = settings?.name || settings?.vesselName || vessel.name || '';
    const type = settings?.type || settings?.vesselType || vessel.type || '';
    const category = (settings?.category || vessel.category || '').toString();

    // IMPORTANT: let detailed settings WIN over basic vessel fields
    const merged = { ...vessel, ...settings, id: vessel.id, name, type, category };

    // FT-269: vessel timezone (IANA name) with safe device-tz fallback.
    merged.timezone = settings?.timezone
      || (typeof Intl !== 'undefined' && Intl.DateTimeFormat
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : 'UTC')
      || 'UTC';

    // Prevent legacy fields leaking to consumers
    delete merged.vesselType;
    delete merged.vesselName;

    return merged;
  }, [vessels, currentVesselId, settingsVersion, readSettings]); // FT-158: settingsVersion for invalidation; FT-263: readSettings

  // Memoized currentVessel - stable reference that only changes when vessel data actually changes
  const currentVessel = useMemo(() => {
    // FT-302: guard against `vessels === undefined` initial state.
    const vessel = (vessels || []).find(v => v.id === currentVesselId);
    if (!vessel) return null;

    // FT-263: read via sentinel-aware cache (IDB-backed) instead of LS-only.
    const settings = readSettings(currentVesselId);

    // Canonical names preferred, with safe fallbacks
    // FT-231: Also check settings?.type (not just vesselType) as that's where type is stored
    // FT-263 (2026-05-31): precedence flipped. Settings is now the canonical
    // store (populated via sr:sync:hydrate from cloud + IDB-backed cache). The
    // `vessels` array is loaded once via legacyGet/LS at mount and never updated
    // by the hydrate listener — so vessel.name reflects the boot-time value.
    // Prefer settings.name first so cross-device edits are visible immediately.
    const name = settings?.name || settings?.vesselName || vessel.name || '';
    const type = settings?.type || settings?.vesselType || vessel.type || '';
    const category = (settings?.category || vessel.category || '').toString();

    // IMPORTANT: let detailed settings WIN over basic vessel fields
    const merged = { ...vessel, ...settings, id: vessel.id, name, type, category };

    // FT-269: vessel timezone (IANA name) with safe device-tz fallback.
    // Stored verbatim from vessel settings; defaults to detected timezone for
    // vessels that have never set one. Used everywhere voyage events are
    // displayed and snapshotted onto event records at write time.
    merged.timezone = settings?.timezone
      || (typeof Intl !== 'undefined' && Intl.DateTimeFormat
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : 'UTC')
      || 'UTC';

    // Prevent legacy fields leaking to consumers
    delete merged.vesselType;
    delete merged.vesselName;

    return merged;
  }, [vessels, currentVesselId, settingsVersion, readSettings]); // FT-158: settingsVersion; FT-263: readSettings

  // FT-231: Enriched vessels array with type/category merged from vesselSettings
  // Used by VesselSelector dropdown to show correct type/category for ALL vessels
  const enrichedVessels = useMemo(() => {
    // FT-302: guard against `vessels === undefined` initial state.
    return (vessels || []).map(vessel => {
      // FT-263: read via sentinel-aware cache (warmed for all known vessels on mount).
      const settings = readSettings(vessel.id);
      // FT-263 (2026-05-31): same precedence flip as currentVessel — settings
      // (cloud-synced via hydrate) wins over the boot-time vessels-array value.
      const name = settings?.name || settings?.vesselName || vessel.name || '';
      const type = settings?.type || settings?.vesselType || vessel.type || '';
      const category = (settings?.category || vessel.category || '').toString();
      return { ...vessel, ...settings, id: vessel.id, name, type, category };
    });
  }, [vessels, settingsVersion, readSettings]);

  // Switch to a different vessel
  const setCurrentVessel = useCallback((vesselId) => {
    setCurrentVesselId(vesselId);
    // FT-365 v3.1 hotfix: raw write (not legacySet). Downstream readers
    // (vesselGuard.js:105, DefectRecordModal.js:18, etc.) use raw LS reads
    // and require a plain UUID; legacySet double-encoded via JSON.stringify.
    // We keep the same-tab notification below (custom event) so consumers
    // still react without waiting for a render tick.
    localStorage.setItem('currentVesselId', vesselId);

    // Emit custom event for other components to react to vessel change
    window.dispatchEvent(new CustomEvent('vesselChanged', { detail: { vesselId } }));
  }, []);

  // Add a new vessel
  const addVessel = useCallback((vesselData) => {
    // SINGLE ORG-SCOPING FIX (2026-02-22): Use UUID for Azure sync compatibility
    const newVessel = {
      ...vesselData,
      id: vesselData.id || crypto.randomUUID(),
      name: vesselData.name || vesselData.vesselName || 'New Vessel', // Support legacy field
      certificateType: vesselData.certificateType || 'workboat', // Certificate-based default
      type: vesselData.type || vesselData.vesselType || 'workboat', // Certificate-based default
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    // Remove legacy fields if they exist
    delete newVessel.vesselName;
    delete newVessel.vesselType;
    
    setVessels(prevVessels => {
      const updatedVessels = [...prevVessels, newVessel];
      legacySet(null, 'vessels', updatedVessels);
      return updatedVessels;
    });
    
    // Initialize storage for the new vessel
    initializeVesselStorage(newVessel.id, newVessel);
    
    return newVessel;
  }, []);

  // Update vessel details
  const updateVessel = useCallback((vesselId, updates) => {
    // Convert legacy field names if present
    const standardizedUpdates = { ...updates };
    if (updates.vesselName !== undefined) {
      standardizedUpdates.name = updates.vesselName;
      delete standardizedUpdates.vesselName;
    }
    if (updates.vesselType !== undefined) {
      standardizedUpdates.type = updates.vesselType;
      delete standardizedUpdates.vesselType;
    }
    
    setVessels(prevVessels => {
      const updatedVessels = prevVessels.map(vessel => 
        vessel.id === vesselId 
          ? { ...vessel, ...standardizedUpdates, updatedAt: new Date().toISOString() }
          : vessel
      );
      
      // Save to storage using legacyUtils
      legacySet(null, 'vessels', updatedVessels);
      
      // Update vessel settings - always create/update, not just if exists.
      // FT-263 (§5.6a): READ via sentinel-aware cache (IDB-backed) so the
      // pre-merge value is the canonical record — NOT a possibly-empty LS read
      // that would silently wipe every unedited field when pushed to cloud.
      // WRITE semantics (legacySet → writeJSON → outbox/push) are unchanged.
      const vesselSettings = readSettings(vesselId) || {};
      const updatedSettings = { ...vesselSettings, ...standardizedUpdates };
      legacySet(vesselId, 'vesselSettings', updatedSettings);
      
      return updatedVessels;
    });
    
    // FT-158: Invalidate currentVessel memoization when settings change
    setSettingsVersion(v => v + 1);

    // FT-263 follow-up (2026-05-31): also update settingsCache synchronously
    // so the same-tab UI reflects the edit immediately. Without this, the
    // currentVessel memo runs but readSettings(vesselId) returns the OLD
    // cached value (the cache only updates when the cloud round-trips back
    // via sr:sync:hydrate). The cross-device path is unaffected — that flow
    // continues to land through the hydrate listener as before; this update
    // just closes the local feedback loop.
    setSettingsCache(prev => ({
      ...prev,
      [vesselId]: { ...(prev[vesselId] || {}), ...standardizedUpdates },
    }));

    // Emit update event
    window.dispatchEvent(new CustomEvent('vesselUpdated', { detail: { vesselId, updates: standardizedUpdates } }));
    // FT-263: readSettings is captured via closure for the §5.6a pre-merge read.
    // Adding it to deps so the merge always uses the latest sentinel-aware cache.
    // Push path semantics (setVessels/legacySet/writeJSON chain) are untouched.
  }, [readSettings]);

  // Delete a vessel
  const deleteVessel = useCallback((vesselId) => {
    let shouldSwitchVessel = false;
    let newVesselId = null;
    
    setVessels(prevVessels => {
      // Prevent deleting the last vessel
      if (prevVessels.length <= 1) {

        return prevVessels;
      }
      
      // Remove vessel from list
      const updatedVessels = prevVessels.filter(v => v.id !== vesselId);
      legacySet(null, 'vessels', updatedVessels);
      
      // Check if we need to switch vessels
      if (currentVesselId === vesselId && updatedVessels.length > 0) {
        shouldSwitchVessel = true;
        newVesselId = updatedVessels[0].id;
      }
      
      return updatedVessels;
    });
    
    // Clean up vessel-scoped data
    cleanupVesselStorage(vesselId);
    
    // If deleted vessel was current, switch to first available
    if (shouldSwitchVessel && newVesselId) {
      setCurrentVessel(newVesselId);
    }
    
    return true;
  }, [currentVesselId, setCurrentVessel]);

  // Get vessel-scoped storage (compatibility layer)
  const getVesselStorage = useCallback((key) => {
    if (!currentVesselId) return null;
    return legacyGet(currentVesselId, key);
  }, [currentVesselId]);

  // Set vessel-scoped storage (compatibility layer)
  const setVesselStorage = useCallback((key, data) => {
    if (!currentVesselId) return false;
    
    try {
      legacySet(currentVesselId, key, data);
      
      // Emit storage update event
      window.dispatchEvent(new CustomEvent('vesselStorageUpdated', { 
        detail: { key, vesselId: currentVesselId } 
      }));
      
      return true;
    } catch (error) {

      return false;
    }
  }, [currentVesselId]);

  // Set vessel settings (compatibility layer)
  const setVesselSettings = useCallback((settings) => {
    if (!currentVesselId) return false;
    
    try {
      // Convert legacy field names if present
      const standardizedSettings = { ...settings };
      if (settings.vesselName !== undefined) {
        standardizedSettings.name = settings.vesselName;
        delete standardizedSettings.vesselName;
      }
      if (settings.vesselType !== undefined) {
        standardizedSettings.type = settings.vesselType;
        delete standardizedSettings.vesselType;
      }
      
      legacySet(currentVesselId, 'vesselSettings', standardizedSettings);
      
      // Also update vessel in vessels array
      updateVessel(currentVesselId, standardizedSettings);
      
      return true;
    } catch (error) {

      return false;
    }
  }, [currentVesselId, updateVessel]);

  // FT-404: memoize the context value so useVessel() consumers only re-render
  // when a member they read actually changes. Previously this was a raw object
  // literal rebuilt on EVERY VesselProvider render, so a single sr:sync:hydrate
  // tick (which bumps settingsVersion on every cloud re-pull of vesselSettings)
  // minted a brand-new value object and force-re-rendered all ~100 useVessel()
  // consumers app-wide — swapping inline onClick handlers on live buttons
  // mid-gesture, the iOS PWA first-tap drop. Every member below is already
  // useState primitive/array or useCallback/useMemo-stable, so the deps array is
  // exactly the 15 members (isMultiVessel/vesselCount derive from `vessels`,
  // which is in the list).
  const value = useMemo(() => ({
    // State
    vessels,
    enrichedVessels,      // FT-231: Vessels with type/category merged from settings
    currentVesselId,
    isLoading,
    isHydrating,          // FT-365 v3.5: true until AuthGate signals initial hydration complete

    // Core functions
    getCurrentVessel,
    currentVessel,        // Stable memoized reference
    setCurrentVessel,
    addVessel,
    updateVessel,
    deleteVessel,

    // Storage functions (compatibility)
    getVesselStorage,
    setVesselStorage,
    getVesselSettings,
    setVesselSettings,

    // Helper functions
    // FT-302: guard against `vessels === undefined` initial state.
    isMultiVessel: (vessels || []).length > 1,
    vesselCount: (vessels || []).length
  }), [
    vessels, enrichedVessels, currentVesselId, isLoading, isHydrating,
    getCurrentVessel, currentVessel, setCurrentVessel, addVessel, updateVessel,
    deleteVessel, getVesselStorage, setVesselStorage, getVesselSettings,
    setVesselSettings,
  ]);

  return (
    <VesselContext.Provider value={value}>
      {children}
    </VesselContext.Provider>
  );
};

export default VesselContext;